/**
 * Celebration Modal component - shown when tutorial is completed
 */

import { useNavigate } from "@remix-run/react";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "#app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#app/components/ui/dialog";
import { type TutorialProgress } from "./ProgressTracker";

export interface CelebrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tutorialTitle: string;
  progress: TutorialProgress | null;
  totalSteps: number;
}

export function CelebrationModal({
  open,
  onOpenChange,
  tutorialTitle,
  progress,
  totalSteps,
}: CelebrationModalProps) {
  const { t } = useTranslation("docs");
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      // Hide confetti animation after 3 seconds
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleContinueLearning = () => {
    onOpenChange(false);
    navigate("/u/alpha/tutorials");
  };

  const timeSpent =
    progress?.startedAt && progress?.completedAt
      ? Math.round((progress.completedAt - progress.startedAt) / 1000 / 60) // minutes
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div
                className="absolute top-0 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-bounce"
                style={{ animationDelay: "0s" }}
              />
              <div
                className="absolute top-0 left-1/2 w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              />
              <div
                className="absolute top-0 left-3/4 w-2 h-2 bg-green-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.4s" }}
              />
              <div
                className="absolute top-0 right-1/4 w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.6s" }}
              />
              <div
                className="absolute top-0 right-1/2 w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.8s" }}
              />
            </div>
          )}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CheckCircle2 className="h-16 w-16 text-green-500 dark:text-green-300" />
              {showConfetti && (
                <Sparkles className="h-8 w-8 text-yellow-400 absolute -top-2 -right-2 animate-pulse" />
              )}
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold text-[#141A42] dark:text-zinc-100">
            {t("congratulations")}
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            {t("tutorialCompleted", { title: tutorialTitle })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-zinc-800">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t("completed")}
            </span>
            <span className="font-semibold text-[#141A42] dark:text-zinc-100">
              {progress?.completedSteps.length || 0} / {totalSteps}
            </span>
          </div>
          {timeSpent !== null && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-zinc-800">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t("timeSpent")}
              </span>
              <span className="font-semibold text-[#141A42] dark:text-zinc-100">
                {t("minutes", { count: timeSpent })}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="gradient-outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {t("close")}
          </Button>
          <Button
            variant="gradient"
            onClick={handleContinueLearning}
            className="w-full sm:w-auto"
          >
            {t("continueLearning")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
